import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Typography,
  Box,
  List,
  ListItem,
  Checkbox,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Select,
  MenuItem,
  Paper,
  Container,
  InputLabel,
  FormControl,
} from "@mui/material";
import {
  selectEditConnectionOperations,
  selectEditConnectionConnectionProfiles,
  selectEditConnectionSelectedPairs,
  setEditConnectionConnectionPairWorkflow,
  selectEditConnectionConnectionPairWorkflow,
  selectEditConnectionConnectionPairs,
} from "@/features/connections/integrationSlice";
import ShepCheckbox from "@/components/GenericComponents/ShepCheckbox";
import ShepGridWarning from "@/components/GenericComponents/ShepGridWarning";

const Workflows = ({ connectionId, initialWorkflowIds }) => {
  const dispatch = useDispatch();
  const [currentConnectionId, setCurrentConnectionId] = useState(null);
  const [currentChecked, setCurrentChecked] = useState([]);
  const [loadedPairs, setLoadedPairs] = useState([]);
  const [leftSideOperations, setLeftSideOperations] = useState([]);
  const [rightSideOperations, setRightSideOperations] = useState([]);
  const allWorkflows = useSelector(selectEditConnectionOperations);
  const selectedPairs = useSelector(selectEditConnectionSelectedPairs);
  const allConnectionPairs = useSelector(selectEditConnectionConnectionPairs);
  const workflowModel = useSelector(selectEditConnectionConnectionPairWorkflow);
  const allConnectionProfiles = useSelector(
    selectEditConnectionConnectionProfiles
  );
  useEffect(() => {
    if (selectedPairs && selectedPairs.length) {
      setLoadedPairs(selectedPairs);
    } else {
      if (allConnectionPairs && allConnectionPairs.length) {
        const connectionPairs = allConnectionPairs.filter(
          (x) =>
            x.ConnectionId1 === connectionId || x.ConnectionId2 === connectionId
        );
        if (connectionPairs && connectionPairs.length) {
          const ids = connectionPairs.map((pair) => {
            const otherConnectionId =
              pair.ConnectionId1 == connectionId
                ? pair.ConnectionId2
                : pair.ConnectionId1;
            return {
              connectionId: otherConnectionId,
              connectionPairName: `${pair.ConnectionName2} | ${pair.ConnectionPairName}`,
              connectionPairId: pair.ConnectionPairId,
            };
          });
          if (ids && ids.length) {
            setLoadedPairs(ids);
          }
        }
      }
    }
  }, [selectedPairs, allConnectionPairs]);
  const setBothSides = (connectionId1, connectionId2) => {
    const left = [];
    const right = [];
    let setLeft = true;
    allWorkflows.forEach((workflow) => {
      let match = false;
      let newModel = null;
      let source = workflow.SourceConnections.filter((x) => x == connectionId1);
      let target = workflow.TargetConnections.filter((x) => x == connectionId2);
      if (source && source.length && target && target.length) {
        match = true;
        newModel = {
          operationId: workflow.OperationId,
          operationName: workflow.OperationName,
          sourceConnectionId: connectionId1,
          targetConnectionId: connectionId2,
        };
      } else {
        let source = workflow.SourceConnections.filter(
          (x) => x == connectionId2
        );
        let target = workflow.TargetConnections.filter(
          (x) => x == connectionId1
        );
        if (source && source.length && target && target.length) {
          match = true;
          newModel = {
            operationId: workflow.OperationId,
            operationName: workflow.OperationName,
            sourceConnectionId: connectionId2,
            targetConnectionId: connectionId1,
          };
        }
      }
      if (match) {
        if (setLeft) {
          left.push(newModel);
        } else {
          right.push(newModel);
        }
        setLeft = !setLeft;
      }
    });
    setLeftSideOperations(left);
    setRightSideOperations(right);
    return [...left, ...right];
  };

  const handleSelectChange = (e) => {
    const id = e.target.value;
    const allSides = setBothSides(id, connectionId);
    setCurrentConnectionId(id);
    if (!workflowModel.hasOwnProperty(id)) {
      const newModel = { ...workflowModel };
      newModel[id] = [];
      const selected = allConnectionPairs.filter(
        (x) =>
          (x.ConnectionId1 === id && x.ConnectionId2 === connectionId) ||
          (x.ConnectionId2 === id && x.ConnectionId1 === connectionId)
      )[0];
      if (selected) {
        const pairProfile = allConnectionProfiles.filter(
          (p) => p.ConnectionPairId === selected.ConnectionPairId
        );

        if (pairProfile && pairProfile.length) {
          const operationIds = pairProfile.map((x) => x.OperationId);
          newModel[id] = allSides.filter(
            (x) => operationIds.indexOf(x.operationId) > -1
          );
        }
      }
      //InitalWorkflowIds is only populated when converting a requested connection to a new conenction
      //selects any applicable workflows the user has requested.
      if (initialWorkflowIds) {
        newModel[id] = allSides.filter((x) =>
          initialWorkflowIds.includes(x.operationId)
        );
      }
      dispatch(setEditConnectionConnectionPairWorkflow(newModel));
    }
  };

  const deleteExisting = (value) => {
    // should be removed when actual deletion is resolved.
    const loadedItem = loadedPairs.filter(
      (x) => x.connectionId === currentConnectionId
    )[0];
    if (!loadedItem.connectionPairId) return false;
    const opertaionId = value.operationId;
    const existingProfile = allConnectionProfiles.filter(
      (x) =>
        x.operationId === opertaionId &&
        x.connectionPairId === loadedItem.connectionPairId
    );
    if (existingProfile && existingProfile.length) {
      return true;
    }
    return false;
  };

  const OperationList = (props) => {
    const handleToggle = (value) => () => {
      if (deleteExisting(value)) {
        alert("cannot uncheck, implement in SHEP-283.");
        return;
      }
      if (currentConnectionId) {
        let newModel = {};
        if (!workflowModel.hasOwnProperty(currentConnectionId)) {
          newModel = { ...workflowModel };
          newModel[currentConnectionId] = [];
        } else {
          newModel = { ...workflowModel };
        }
        const newChecked = [...newModel[currentConnectionId]];
        const currentIndex = newChecked.findIndex(
          (x) => x.operationId === value.operationId
        );
        if (currentIndex === -1) {
          newChecked.push(value);
        } else {
          newChecked.splice(currentIndex, 1);
        }
        newModel[currentConnectionId] = newChecked;
        dispatch(setEditConnectionConnectionPairWorkflow(newModel));
      }
    };

    useEffect(() => {
      if (workflowModel && currentConnectionId) {
        setCurrentChecked(workflowModel[currentConnectionId]);
      }
    }, [workflowModel, currentConnectionId]);

    return (
      <List
        sx={{
          width: "100%",
          maxWidth: 600,
          bgcolor: "transparent",
        }}
      >
        {props.operations.map((value) => {
          const labelId = `checkbox-list-label-${value.operationId}`;

          return (
            <ListItem key={value.operationId}>
              <ListItemButton
                role={undefined}
                onClick={handleToggle(value)}
                dense
              >
                <ListItemIcon>
                  <ShepCheckbox
                    edge="start"
                    disabled={false}
                    checked={
                      currentChecked &&
                      currentChecked.filter(
                        (x) => x.operationId == value.operationId
                      ).length > 0
                    }
                    tabIndex={-1}
                    disableRipple
                    inputProps={{ "aria-labelledby": labelId }}
                  />
                </ListItemIcon>
                <ListItemText
                  id={labelId}
                  primary={value.operationName}
                ></ListItemText>
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    );
  };

  return (
    <>
      <Container style={{ paddingTop: 5 }}>
        <Box
          sx={{
            height: "78px",
            backgroundColor: "var(--primary-main)",
            color: "var(--default-light)",
            padding: "20px 0px 20px 53px",
            borderRadius: "4px",
            border: "1px black",
            boxSizing: "border-box",
          }}
        >
          <Typography
            style={{
              fontSize: "var(--font-lg)",
              fontWeight: "var(--font-semi-bold)",
            }}
          >
            Workflows
          </Typography>
        </Box>
        <Box style={{ marginTop: 25, marginBottom: 25 }}>
          <FormControl>
            <InputLabel id="connectionPairList">
              Select Connection Pair
            </InputLabel>
            <Select
              style={{ width: "400px" }}
              id="connectionPairList"
              onChange={handleSelectChange}
              MenuProps={{
                style: { zIndex: 1501 },
              }}
            >
              {loadedPairs.map((pair) => {
                return (
                  <MenuItem key={pair.connectionId} value={pair.connectionId}>
                    {pair.connectionPairName}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Box>
      </Container>
      {!currentConnectionId && (
        <ShepGridWarning
          title="No Connection Pair Selected."
          message="Select a connection pair to display the workflows."
        />
      )}
      {
        //SHEP-426 Reuse the following message style when no workflows
        currentConnectionId != null &&
        leftSideOperations.length == 0 &&
        rightSideOperations.length == 0 && (
          <ShepGridWarning
            title="No Valid Workflows."
            message="Selected connection pair has no valid workflows."
          />
        )
      }
      {Object.keys(loadedPairs).length > 0 && (
        <Paper>
          <Stack direction="row">
            <OperationList operations={leftSideOperations} />
            <OperationList operations={rightSideOperations} />
          </Stack>
        </Paper>
      )}
    </>
  );
};
export default Workflows;
